import { ChatbotMode, ChatbotState, WalkthroughFlow } from 'models/chatbot';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export const initialState: ChatbotState = {
  isOpen: false,
  displayMode: 'floating',
  isMinimized: false,
  messages: [],
  historyMessages: [],
  loading: false,
  selectedModel: '',
  models: [],
  useNeighbors: true,
  tools: [],
  temperature: 0.0,
  suppressHistory: false,
  toolsEnabled: true,
  suppressContext: false,
  onlyValidated: true,
  sessionId: '',
  hasChatbotAdvancedConfig: undefined,
  feedbackStates: {},
  walkthrough: {
    isActive: false,
    flowId: null,
    currentStepId: null,
    previousStepId: null,
    steps: [],
    refreshKey: 0,
  },
};

const limitHistory = (history: ChatbotState['historyMessages']) => {
  if (history.length > 5) {
    return history.slice(-5);
  }
  return history;
};

export const slice = createSlice({
  name: 'chatbot',
  initialState,
  reducers: {
    reset: () => initialState,
    setSessionId: (state, action: PayloadAction<string>) => {
      state.sessionId = action.payload;
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    setDisplayMode: (state, action: PayloadAction<ChatbotMode>) => {
      state.displayMode = action.payload;
    },
    setMinimized: (state, action: PayloadAction<boolean>) => {
      state.isMinimized = action.payload;
    },
    clearMessages: (state) => {
      const newMessages = state.messages.filter(
        (msg) =>
          !state.historyMessages.some(
            (historyMsg) => historyMsg.question === msg.question && historyMsg.answer === msg.answer,
          ),
      );
      state.historyMessages = limitHistory([...state.historyMessages, ...newMessages]);
      state.messages = [];
    },
    addMessage: (state, action: PayloadAction<{ question: string; answer: string | null }>) => {
      state.messages.push(action.payload);
    },
    updateLastMessage: (state, action: PayloadAction<string>) => {
      if (state.messages.length > 0) {
        state.messages[state.messages.length - 1].answer = action.payload;
      }
    },
    updateLastMessageWithAttr: (
      state,
      action: PayloadAction<{
        answer: string;
        responseTime?: number;
        links?: {
          document: string;
          page: number;
          url: string;
        }[];
        previews?: any[];
        attachments?: any[];
        chatHistoryId: string;
        walkthroughFlow?: WalkthroughFlow;
        images?: any[];
        audios?: any[];
      }>,
    ) => {
      if (state.messages.length > 0) {
        const lastMessage = state.messages[state.messages.length - 1];
        lastMessage.answer = action.payload.answer;
        lastMessage.responseTime = action.payload.responseTime;
        lastMessage.links = action.payload.links;
        lastMessage.previews = action.payload.previews;
        lastMessage.attachments = action.payload.attachments;
        lastMessage.chatHistoryId = action.payload.chatHistoryId;
        lastMessage.walkthroughFlow = action.payload.walkthroughFlow;
        lastMessage.images = action.payload.images;
        lastMessage.audios = action.payload.audios;
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setSelectedModel: (state, action: PayloadAction<string>) => {
      state.selectedModel = action.payload;
    },
    setUseNeighbors: (state, action: PayloadAction<boolean>) => {
      state.useNeighbors = action.payload;
    },
    setTools: (state, action: PayloadAction<any[]>) => {
      state.tools = action.payload;
    },
    setModels: (state, action: PayloadAction<string[]>) => {
      state.models = action.payload;
    },
    setTemperature: (state, action: PayloadAction<number>) => {
      state.temperature = action.payload;
    },
    setSuppressHistory: (state, action: PayloadAction<boolean>) => {
      state.suppressHistory = action.payload;
    },
    setToolsEnabled: (state, action: PayloadAction<boolean>) => {
      state.toolsEnabled = action.payload;
    },
    setSuppressContext: (state, action: PayloadAction<boolean>) => {
      state.suppressContext = action.payload;
    },
    clearHistory: (state) => {
      state.historyMessages = [];
    },
    setOnlyValidated: (state, action: PayloadAction<boolean>) => {
      state.onlyValidated = action.payload;
    },
    setHasChatbotAdvancedConfig: (state, action: PayloadAction<boolean>) => {
      state.hasChatbotAdvancedConfig = action.payload;
    },
    setFeedbackState: (
      state,
      action: PayloadAction<{ chatHistoryId: string; feedbackType: 'up' | 'down' | 'flag' | null }>,
    ) => {
      state.feedbackStates[action.payload.chatHistoryId] = action.payload.feedbackType;
    },
    startWalkthrough: (state, action: PayloadAction<WalkthroughFlow>) => {
      state.walkthrough = {
        isActive: true,
        flowId: action.payload.id,
        currentStepId: action.payload.steps[0]?.id || null,
        previousStepId: null,
        steps: action.payload.steps,
        refreshKey: 0,
      };
      state.displayMode = 'floating';
      state.isMinimized = true;
    },
    nextWalkthroughStep: (state) => {
      const currentStep = state.walkthrough.steps.find((s) => s.id === state.walkthrough.currentStepId);
      if (currentStep?.next) {
        state.walkthrough.previousStepId = state.walkthrough.currentStepId;
        state.walkthrough.currentStepId = currentStep.next;
      } else if (currentStep?.end) {
        state.walkthrough.isActive = false;
        state.walkthrough.flowId = null;
        state.walkthrough.currentStepId = null;
        state.walkthrough.previousStepId = null;
        state.walkthrough.steps = [];
      }
    },
    closeWalkthrough: (state) => {
      state.walkthrough.isActive = false;
      state.walkthrough.flowId = null;
      state.walkthrough.currentStepId = null;
      state.walkthrough.previousStepId = null;
      state.walkthrough.steps = [];
      state.displayMode = 'floating';
      state.isMinimized = false;
    },
    forceWalkthroughRefresh: (state) => {
      state.walkthrough.refreshKey += 1;
    },
  },
});

export default slice.reducer;
